/**
 * Entry point: boots the main menu, and wires solo/multiplayer sessions into
 * the renderer/input/hud trio once a game actually starts.
 */

import './style.css';
import { Advisor } from './advisor';
import { initAudio, setScreen as setAudioScreen } from './audio';
import { CopilotPanel } from './copilot';
import { Hud } from './hud';
import { InputController } from './input';
import { renderMainMenu } from './menu';
import { Renderer } from './renderer';
import { GameSession } from './session';

function boot(): void {
  initAudio();
  // iOS Safari ignores user-scalable=no; kill its page pinch/double-tap zoom
  // outright (game zoom on the canvas has its own pinch handler).
  for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(ev, (e) => e.preventDefault(), { passive: false });
  }
  document.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
  const menuRoot = document.getElementById('menu-root') as HTMLElement;
  const gameRoot = document.getElementById('game-root') as HTMLElement;
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const hudRoot = document.getElementById('hud-root') as HTMLElement;
  const toastRoot = document.getElementById('toast-root') as HTMLElement;

  let session: GameSession | null = null;
  let renderer: Renderer | null = null;
  let input: InputController | null = null;
  let hud: Hud | null = null;
  let advisor: Advisor | null = null;
  let copilotPanel: CopilotPanel | null = null;
  let rafId = 0;

  function showMenu(): void {
    if (rafId) cancelAnimationFrame(rafId);
    session?.destroy();
    session = null;
    renderer = null;
    input = null;
    hud = null;
    advisor = null;
    copilotPanel = null;
    gameRoot.hidden = true;
    menuRoot.hidden = false;
    setAudioScreen('menu');
    renderMainMenu(menuRoot, {
      onStartSolo: (config) => {
        const s = new GameSession();
        s.startSolo(config, 0);
        enterGame(s);
      },
      onStartMultiplayer: (s) => enterGame(s),
    });
  }

  function enterGame(activeSession: GameSession): void {
    session = activeSession;
    menuRoot.hidden = true;
    gameRoot.hidden = false;
    setAudioScreen('game');

    hud = new Hud(hudRoot, toastRoot, session, () => showMenu(), () => advisor?.openHelp(), () => copilotPanel?.open());
    advisor = new Advisor(hudRoot, session);
    copilotPanel = new CopilotPanel(hudRoot, () => session);
    renderer = new Renderer(canvas, hud.minimapCanvas, session, () => hud?.selectionPanelRect() ?? null);
    input = new InputController(canvas, hud.minimapCanvas, session, {
      onToggleScoreboard: () => hud?.toggleScoreboard(),
    });

    loop();
  }

  function loop(): void {
    renderer?.render();
    input?.tick();
    session?.drainNetworkTicks();
    hud?.update();
    advisor?.update();
    copilotPanel?.update();
    rafId = requestAnimationFrame(loop);
  }

  showMenu();
}

boot();
